import {
    BanknotesIcon,
    CalendarDaysIcon,
    ChatBubbleLeftRightIcon,
    ClipboardDocumentCheckIcon,
    ClockIcon,
    CurrencyDollarIcon,
    DocumentTextIcon,
    InboxStackIcon,
    MapPinIcon,
    PhoneArrowUpRightIcon,
    PhoneIcon,
    ShieldCheckIcon,
    TruckIcon,
    UserPlusIcon,
} from "@heroicons/react/24/outline";
import type { ComponentType } from "react";
import { INDUSTRY_USE_CASES, type IndustryUseCase } from "./industryUseCases";

/**
 * Ad copy and question wording for the `/get-started/*` signup pages.
 *
 * One entry per vertical plus `any` for `/get-started`, which asks the
 * visitor which industry they are in rather than asking a vertical
 * sub-question. Adding a vertical is an entry here plus the matching entry in
 * `backend/src/services/signup/industries.ts` — the two share ids, and the
 * backend is what validates them.
 *
 * Structural fields (label, icon, accent, slug) are read off
 * `INDUSTRY_USE_CASES` so a vertical cannot be branded one way on
 * `/use-cases/:slug` and another way on its signup page. Only the ad copy,
 * the segment wording and the seeded agent text live here.
 *
 * These pages are `noindex`. They are campaign destinations, and
 * `/use-cases/:slug` is the page that carries the organic ranking for the
 * same vertical.
 */

export const GENERIC_CAMPAIGN = "any";

/** `callWidgetBlueprints.ts` keys the guided setup wizard offers. */
export type BlueprintKey = "appointment_booking" | "lead_generation" | "customer_support";

export interface SignupChoice {
    id: string;
    label: string;
}

export interface SignupBenefit {
    Icon: ComponentType<{ className?: string }>;
    text: string;
}

export interface SignupCampaign {
    /** Campaign id. An industry id, or `any`. */
    id: string;
    /** URL segment under `/get-started/`. */
    slug: string;
    /** The vertical this campaign sells to, or null on the generic page. */
    industry: string | null;
    Icon: ComponentType<{ className?: string }>;
    /** Small uppercase line above the headline. */
    eyebrow: string;
    /** The ad's promise, repeated so the click and the page agree. */
    headline: string;
    subhead: string;
    benefits: readonly SignupBenefit[];
    /** The vertical's own question. Absent on the generic page. */
    segment: { legend: string; options: readonly SignupChoice[] } | null;
    /** Wording for the shared `callHandling` scale. */
    callHandling: { legend: string; options: readonly SignupChoice[] };
    businessNamePlaceholder: string;
    emailPlaceholder: string;
    websitePlaceholder: string;
    /** Which guided-setup blueprint the new workspace starts on. */
    blueprint: BlueprintKey;
    /** What the agent is called, in the wizard and the onboarding copy. */
    agentNoun: string;
    /** Completes "You answer calls for {business}, a {segment} business. …" */
    agentTask: string;
    seo: { title: string; description: string };
}

const TEAM_SIZE_LEGEND = "Team size";

export const TEAM_SIZE_OPTIONS: readonly SignupChoice[] = [
    { id: "solo", label: "Just me" },
    { id: "2-5", label: "2–5" },
    { id: "6-20", label: "6–20" },
    { id: "20+", label: "20+" },
];

export const VOLUME_LEGEND = "Calls and messages a week";

export const VOLUME_OPTIONS: readonly SignupChoice[] = [
    { id: "under_25", label: "Under 25" },
    { id: "25_100", label: "25–100" },
    { id: "100_500", label: "100–500" },
    { id: "500_plus", label: "500+" },
];

export { TEAM_SIZE_LEGEND };

/**
 * The shared call-handling scale. Every campaign answers the same five ids;
 * only the wording of "someone on staff" changes, because that is the option
 * whose name differs most between a roofing crew and a dental front desk.
 */
const callHandling = (legend: string, staffLabel: string, selfLabel = "I answer myself") => ({
    legend,
    options: [
        { id: "voicemail", label: "Voicemail" },
        { id: "answering_service", label: "Answering service" },
        { id: "staff", label: staffLabel },
        { id: "myself", label: selfLabel },
        { id: "unanswered", label: "They go unanswered" },
    ] as const,
});

const segments = (legend: string, options: readonly SignupChoice[]) => ({ legend, options });

type CampaignSeed = Omit<SignupCampaign, "slug" | "Icon"> & { Icon?: ComponentType<{ className?: string }> };

const CAMPAIGN_SEEDS: readonly CampaignSeed[] = [
    {
        id: "auto_dealers",
        industry: "auto_dealers",
        eyebrow: "For dealerships",
        headline: "Every missed call is a car you didn't sell.",
        subhead: "FloGPT answers 24/7, qualifies the buyer, and books the test drive.",
        benefits: [
            { Icon: PhoneIcon, text: "Answers after hours and through weekend sales spikes." },
            { Icon: CalendarDaysIcon, text: "Books the test drive into your calendar." },
            { Icon: ChatBubbleLeftRightIcon, text: "Texts the vehicle listing while they're still interested." },
        ],
        segment: segments("What do you sell?", [
            { id: "new_vehicles", label: "New vehicles" },
            { id: "used_vehicles", label: "Used vehicles" },
            { id: "new_and_used", label: "New and used" },
            { id: "service_and_parts", label: "Service and parts" },
            { id: "other", label: "Other" },
        ]),
        callHandling: callHandling("How are calls handled after hours?", "Sales staff"),
        businessNamePlaceholder: "Sunrise Motors",
        emailPlaceholder: "jane@sunrisemotors.com",
        websitePlaceholder: "sunrisemotors.com",
        blueprint: "lead_generation",
        agentNoun: "AI sales assistant",
        agentTask: "Qualify the vehicle they want, capture any trade-in, and book the test drive.",
        seo: {
            title: "AI Sales Assistant for Dealerships | FloGPT",
            description:
                "Answer every showroom call, qualify the buyer, capture the trade-in, and book the test drive. Set up an AI assistant for your dealership in minutes.",
        },
    },
    {
        id: "dental_healthcare",
        industry: "dental_healthcare",
        eyebrow: "For practices",
        headline: "Your front desk can't answer every call.",
        subhead: "FloGPT answers 24/7, books the appointment, and confirms by text.",
        benefits: [
            { Icon: PhoneIcon, text: "Answers while the front desk is with a patient." },
            { Icon: CalendarDaysIcon, text: "Books into your schedule and sends a reminder." },
            { Icon: ShieldCheckIcon, text: "Routes urgent calls to a person. Never gives medical advice." },
        ],
        segment: segments("What kind of practice?", [
            { id: "dental", label: "Dental" },
            { id: "medical_practice", label: "Medical" },
            { id: "specialty_clinic", label: "Specialty clinic" },
            { id: "med_spa", label: "Med spa" },
            { id: "other", label: "Other" },
        ]),
        callHandling: callHandling("How are calls handled when the desk is busy?", "Front desk staff"),
        businessNamePlaceholder: "Lakeside Dental",
        emailPlaceholder: "jane@lakesidedental.com",
        websitePlaceholder: "lakesidedental.com",
        blueprint: "appointment_booking",
        agentNoun: "AI receptionist",
        agentTask:
            "Book and reschedule appointments, and hand urgent calls to a person. Never give clinical advice.",
        seo: {
            title: "AI Receptionist for Dental & Medical Practices | FloGPT",
            description:
                "Answer every patient call, book and reschedule appointments, and route urgent cases to staff. Set up an AI receptionist for your practice in minutes.",
        },
    },
    {
        id: "education",
        industry: "education",
        eyebrow: "For schools and programmes",
        headline: "Prospective students call once.",
        subhead: "FloGPT answers 24/7, answers the programme questions, and books the counsellor call.",
        benefits: [
            { Icon: PhoneIcon, text: "Answers enquiries evenings and through application season." },
            { Icon: CalendarDaysIcon, text: "Books the counsellor or admissions call." },
            { Icon: DocumentTextIcon, text: "Answers fee, deadline and requirement questions from your own pages." },
        ],
        segment: segments("What do you run?", [
            { id: "k12", label: "K-12 school" },
            { id: "higher_ed", label: "College or university" },
            { id: "tutoring", label: "Tutoring" },
            { id: "online_courses", label: "Online courses" },
            { id: "other", label: "Other" },
        ]),
        callHandling: callHandling("How are enquiry calls handled now?", "Admissions staff"),
        businessNamePlaceholder: "Northgate Academy",
        emailPlaceholder: "jane@northgate.edu",
        websitePlaceholder: "northgate.edu",
        blueprint: "lead_generation",
        agentNoun: "AI admissions assistant",
        agentTask: "Answer programme, fee and deadline questions, then book a counsellor call.",
        seo: {
            title: "AI Admissions Assistant for Schools | FloGPT",
            description:
                "Answer every prospective-student enquiry, explain programmes and deadlines, and book the counsellor call. Set up an AI admissions assistant in minutes.",
        },
    },
    {
        id: "fitness_wellness",
        industry: "fitness_wellness",
        eyebrow: "For gyms and studios",
        headline: "Nobody's at the desk to take the call.",
        subhead: "FloGPT answers 24/7, books the class or session, and texts the details.",
        benefits: [
            { Icon: PhoneIcon, text: "Answers while your team is on the floor." },
            { Icon: CalendarDaysIcon, text: "Books classes, trials and personal-training sessions." },
            { Icon: ChatBubbleLeftRightIcon, text: "Texts the time, the address and what to bring." },
        ],
        segment: segments("What do you run?", [
            { id: "gym", label: "Gym" },
            { id: "studio", label: "Studio" },
            { id: "personal_training", label: "Personal training" },
            { id: "spa_wellness", label: "Spa and wellness" },
            { id: "other", label: "Other" },
        ]),
        callHandling: callHandling("How are calls handled when the desk is empty?", "Front desk staff"),
        businessNamePlaceholder: "Ridgeline Fitness",
        emailPlaceholder: "jane@ridgelinefitness.com",
        websitePlaceholder: "ridgelinefitness.com",
        blueprint: "appointment_booking",
        agentNoun: "AI front desk",
        agentTask: "Book classes, trials and sessions, and text the caller the details.",
        seo: {
            title: "AI Front Desk for Gyms & Studios | FloGPT",
            description:
                "Answer every call, book classes, trials and personal-training sessions, and text the details. Set up an AI front desk for your studio in minutes.",
        },
    },
    {
        id: "general_support",
        industry: "general_support",
        eyebrow: "For support teams",
        headline: "The same questions, over and over.",
        subhead: "FloGPT answers 24/7, resolves the repeat tickets, and escalates the rest with context.",
        benefits: [
            { Icon: InboxStackIcon, text: "Resolves repeat questions on calls, chat and email." },
            { Icon: DocumentTextIcon, text: "Answers from your own docs, with citations." },
            { Icon: UserPlusIcon, text: "Hands the hard ones to an agent with the full history." },
        ],
        segment: segments("What are you supporting?", [
            { id: "software", label: "Software" },
            { id: "online_store", label: "Online store" },
            { id: "services", label: "Services" },
            { id: "other", label: "Other" },
        ]),
        callHandling: callHandling("How is after-hours support handled?", "Support staff"),
        businessNamePlaceholder: "Northwind Software",
        emailPlaceholder: "jane@northwind.com",
        websitePlaceholder: "northwind.com",
        blueprint: "customer_support",
        agentNoun: "AI support agent",
        agentTask: "Answer from the knowledge base, and escalate to a human with the conversation attached.",
        seo: {
            title: "AI Support Agent for Any Team | FloGPT",
            description:
                "Resolve repeat support questions on calls, chat and email, grounded in your own documentation, and escalate the rest with full context.",
        },
    },
    {
        id: "home_services",
        industry: "home_services",
        eyebrow: "For home-service crews",
        headline: "Missed calls = lost jobs.",
        subhead: "FloGPT answers 24/7, books the job, texts them back.",
        benefits: [
            { Icon: PhoneIcon, text: "Answers the call when you're on a job or it's after hours." },
            { Icon: CalendarDaysIcon, text: "Books the job into your schedule." },
            { Icon: ChatBubbleLeftRightIcon, text: "Texts the customer back with the details." },
        ],
        segment: segments("Trade", [
            { id: "hvac", label: "HVAC" },
            { id: "plumbing", label: "Plumbing" },
            { id: "electrical", label: "Electrical" },
            { id: "roofing", label: "Roofing" },
            { id: "other", label: "Other" },
        ]),
        callHandling: callHandling(
            "How are calls handled when you're on a job or after hours?",
            "Family or office staff",
        ),
        businessNamePlaceholder: "Acme HVAC",
        emailPlaceholder: "jane@acmehvac.com",
        websitePlaceholder: "acmehvac.com",
        blueprint: "appointment_booking",
        agentNoun: "AI receptionist",
        agentTask: "Book the job and text the caller back with the details.",
        seo: {
            title: "AI Receptionist for Home Services | FloGPT",
            description:
                "Missed calls are lost jobs. FloGPT answers 24/7, books the job, and texts the customer back. For HVAC, plumbing, electrical, and roofing crews.",
        },
    },
    {
        id: "hospitality",
        industry: "hospitality",
        eyebrow: "For hotels and rentals",
        headline: "Guests call at 2am.",
        subhead: "FloGPT answers 24/7, in their language, and handles the booking question.",
        benefits: [
            { Icon: ClockIcon, text: "Answers overnight and through check-in rushes." },
            { Icon: ChatBubbleLeftRightIcon, text: "Replies in the guest's own language." },
            { Icon: CalendarDaysIcon, text: "Handles availability, check-in times and add-ons." },
        ],
        segment: segments("What do you run?", [
            { id: "hotel", label: "Hotel" },
            { id: "vacation_rental", label: "Vacation rental" },
            { id: "bed_and_breakfast", label: "Bed and breakfast" },
            { id: "resort", label: "Resort" },
            { id: "other", label: "Other" },
        ]),
        callHandling: callHandling("How are overnight calls handled?", "Front desk staff"),
        businessNamePlaceholder: "Harbour View Hotel",
        emailPlaceholder: "jane@harbourview.com",
        websitePlaceholder: "harbourview.com",
        blueprint: "customer_support",
        agentNoun: "AI guest agent",
        agentTask:
            "Answer availability, check-in and amenity questions in the guest's language, and escalate anything on-property.",
        seo: {
            title: "AI Guest Agent for Hotels & Rentals | FloGPT",
            description:
                "Answer every guest call and message around the clock, in their language — availability, check-in times, amenities and add-ons. Set up in minutes.",
        },
    },
    {
        id: "insurance",
        industry: "insurance",
        eyebrow: "For agencies",
        headline: "Quote calls don't wait for office hours.",
        subhead: "FloGPT answers 24/7, captures the quote request, and routes active claims.",
        benefits: [
            { Icon: PhoneIcon, text: "Answers quote calls evenings and weekends." },
            { Icon: ClipboardDocumentCheckIcon, text: "Captures everything the quote needs, in order." },
            { Icon: ShieldCheckIcon, text: "Routes claims to a person. Never quotes a rate itself." },
        ],
        segment: segments("What do you write?", [
            { id: "auto_and_home", label: "Auto and home" },
            { id: "health", label: "Health" },
            { id: "life", label: "Life" },
            { id: "commercial", label: "Commercial" },
            { id: "other", label: "Other" },
        ]),
        callHandling: callHandling("How are after-hours calls handled?", "Agency staff"),
        businessNamePlaceholder: "Keystone Insurance",
        emailPlaceholder: "jane@keystoneins.com",
        websitePlaceholder: "keystoneins.com",
        blueprint: "lead_generation",
        agentNoun: "AI intake agent",
        agentTask: "Capture what a quote needs and route active claims to a person. Never quote a rate.",
        seo: {
            title: "AI Intake Agent for Insurance Agencies | FloGPT",
            description:
                "Answer every quote call, capture what the quote needs, and route active claims to your team — without ever quoting a rate. Set up in minutes.",
        },
    },
    {
        id: "legal",
        industry: "legal",
        eyebrow: "For law firms",
        headline: "The first firm to answer gets the case.",
        subhead: "FloGPT answers 24/7, runs intake, and books the consultation.",
        benefits: [
            { Icon: PhoneIcon, text: "Answers the moment they call, including nights and weekends." },
            { Icon: ClipboardDocumentCheckIcon, text: "Runs your intake questions and flags conflicts." },
            { Icon: CalendarDaysIcon, text: "Books the consultation. Never gives legal advice." },
        ],
        segment: segments("Practice area", [
            { id: "personal_injury", label: "Personal injury" },
            { id: "family", label: "Family law" },
            { id: "immigration", label: "Immigration" },
            { id: "business_law", label: "Business law" },
            { id: "other", label: "Other" },
        ]),
        callHandling: callHandling("How are after-hours calls handled?", "Firm staff"),
        businessNamePlaceholder: "Hale & Byrne",
        emailPlaceholder: "jane@halebyrne.com",
        websitePlaceholder: "halebyrne.com",
        blueprint: "customer_support",
        agentNoun: "AI intake agent",
        agentTask: "Run intake, flag potential conflicts, and book a consultation. Never give legal advice.",
        seo: {
            title: "AI Intake Agent for Law Firms | FloGPT",
            description:
                "Answer every potential-client call, run your intake questions, flag conflicts, and book the consultation — without giving legal advice.",
        },
    },
    {
        id: "logistics",
        industry: "logistics",
        eyebrow: "For carriers and 3PLs",
        headline: "\"Where is my shipment?\", a thousand times a day.",
        subhead: "FloGPT answers 24/7, gives the real status, and files the exception.",
        benefits: [
            { Icon: TruckIcon, text: "Answers status calls without a dispatcher picking up." },
            { Icon: MapPinIcon, text: "Reschedules deliveries and confirms the window." },
            { Icon: ClipboardDocumentCheckIcon, text: "Files damage and exception claims with the details attached." },
        ],
        segment: segments("What do you move?", [
            { id: "freight", label: "Freight" },
            { id: "last_mile", label: "Last-mile delivery" },
            { id: "warehousing", label: "Warehousing" },
            { id: "moving", label: "Moving" },
            { id: "other", label: "Other" },
        ]),
        callHandling: callHandling("How are status calls handled now?", "Dispatch staff"),
        businessNamePlaceholder: "Meridian Freight",
        emailPlaceholder: "jane@meridianfreight.com",
        websitePlaceholder: "meridianfreight.com",
        blueprint: "customer_support",
        agentNoun: "AI dispatch agent",
        agentTask: "Give shipment status, reschedule deliveries, and open exception claims.",
        seo: {
            title: "AI Dispatch Agent for Logistics | FloGPT",
            description:
                "Answer shipment-status calls around the clock, reschedule deliveries, and file damage and exception claims — without tying up dispatch.",
        },
    },
    {
        id: "real_estate",
        industry: "real_estate",
        eyebrow: "For brokerages and agents",
        headline: "The lead calls while you're at a showing.",
        subhead: "FloGPT answers 24/7, qualifies the buyer or seller, and books the viewing.",
        benefits: [
            { Icon: PhoneIcon, text: "Answers listing enquiries while you're with a client." },
            { Icon: ClipboardDocumentCheckIcon, text: "Qualifies budget, timeline and financing." },
            { Icon: CalendarDaysIcon, text: "Books the showing and pushes the lead to your CRM." },
        ],
        segment: segments("What do you work on?", [
            { id: "residential_sales", label: "Residential sales" },
            { id: "property_management", label: "Property management" },
            { id: "commercial", label: "Commercial" },
            { id: "new_developments", label: "New developments" },
            { id: "other", label: "Other" },
        ]),
        callHandling: callHandling("How are calls handled when you're showing?", "Office staff"),
        businessNamePlaceholder: "Beacon Realty",
        emailPlaceholder: "jane@beaconrealty.com",
        websitePlaceholder: "beaconrealty.com",
        blueprint: "lead_generation",
        agentNoun: "AI assistant",
        agentTask: "Qualify budget, timeline and financing, then book a showing.",
        seo: {
            title: "AI Assistant for Real Estate Agents | FloGPT",
            description:
                "Answer every listing enquiry, qualify budget and timeline, book the showing, and push the lead into your CRM. Set up in minutes.",
        },
    },
    {
        id: "restaurants",
        industry: "restaurants",
        eyebrow: "For restaurants",
        headline: "The phone rings through dinner service.",
        subhead: "FloGPT answers 24/7, takes the reservation, and keeps your host on the floor.",
        benefits: [
            { Icon: PhoneIcon, text: "Answers through service, when nobody can reach the phone." },
            { Icon: CalendarDaysIcon, text: "Takes reservations and waitlist signups." },
            { Icon: ChatBubbleLeftRightIcon, text: "Answers hours, menu and parking questions." },
        ],
        segment: segments("What do you run?", [
            { id: "full_service", label: "Full-service" },
            { id: "quick_service", label: "Quick-service" },
            { id: "multi_location", label: "Multi-location" },
            { id: "catering", label: "Catering" },
            { id: "other", label: "Other" },
        ]),
        callHandling: callHandling("How is the phone handled during service?", "Host or staff"),
        businessNamePlaceholder: "Olive & Ash",
        emailPlaceholder: "jane@oliveandash.com",
        websitePlaceholder: "oliveandash.com",
        blueprint: "appointment_booking",
        agentNoun: "AI host",
        agentTask: "Take reservations and waitlist signups, and answer hours and menu questions.",
        seo: {
            title: "AI Host for Restaurants | FloGPT",
            description:
                "Answer the phone through service, take reservations and waitlist signups, and handle hours and menu questions. Set up an AI host in minutes.",
        },
    },
    {
        id: "retail",
        industry: "retail",
        eyebrow: "For retailers",
        headline: "\"Where's my order?\" shouldn't need a human.",
        subhead: "FloGPT answers 24/7, handles orders, returns and product questions.",
        benefits: [
            { Icon: InboxStackIcon, text: "Answers order, tracking and return questions on its own." },
            { Icon: CurrencyDollarIcon, text: "Looks up the real order, not a canned reply." },
            { Icon: BanknotesIcon, text: "Recovers the sale with a product answer while they're deciding." },
        ],
        segment: segments("Where do you sell?", [
            { id: "online_store", label: "Online store" },
            { id: "physical_store", label: "Physical store" },
            { id: "online_and_physical", label: "Both" },
            { id: "other", label: "Other" },
        ]),
        callHandling: callHandling("How is after-hours support handled?", "Support staff"),
        businessNamePlaceholder: "Field & Folk",
        emailPlaceholder: "jane@fieldandfolk.com",
        websitePlaceholder: "fieldandfolk.com",
        blueprint: "customer_support",
        agentNoun: "AI support agent",
        agentTask: "Look up orders, handle returns and tracking, and answer product questions.",
        seo: {
            title: "AI Support Agent for Retail & Ecommerce | FloGPT",
            description:
                "Resolve order, tracking, return and product questions around the clock, against the real order record. Set up an AI support agent in minutes.",
        },
    },
    {
        id: GENERIC_CAMPAIGN,
        industry: null,
        Icon: PhoneArrowUpRightIcon,
        eyebrow: "For any business",
        headline: "Stop losing customers to a missed call.",
        subhead: "FloGPT answers 24/7, books the work, and follows up by text.",
        benefits: [
            { Icon: PhoneIcon, text: "Answers every call, after hours and at peak." },
            { Icon: CalendarDaysIcon, text: "Books the appointment straight into your calendar." },
            { Icon: ChatBubbleLeftRightIcon, text: "Follows up by text so the lead doesn't go cold." },
        ],
        // The industry question takes this slot on the generic page.
        segment: null,
        callHandling: callHandling("How are calls handled outside business hours?", "Office staff"),
        businessNamePlaceholder: "Acme Co",
        emailPlaceholder: "jane@acme.com",
        websitePlaceholder: "acme.com",
        blueprint: "appointment_booking",
        agentNoun: "AI receptionist",
        agentTask: "Answer the call, book the work, and text the caller a confirmation.",
        seo: {
            title: "AI Receptionist for Your Business | FloGPT",
            description:
                "FloGPT answers every call 24/7, books the appointment, and follows up by text. Pick your industry and set up an AI receptionist in minutes.",
        },
    },
];

const useCaseFor = (industry: string | null): IndustryUseCase | null =>
    industry ? (INDUSTRY_USE_CASES.find((entry) => entry.key === industry) ?? null) : null;

export const SIGNUP_CAMPAIGNS: readonly SignupCampaign[] = CAMPAIGN_SEEDS.map((seed) => {
    const useCase = useCaseFor(seed.industry);
    return {
        ...seed,
        slug: useCase?.slug ?? seed.id.replace(/_/g, "-"),
        Icon: seed.Icon ?? useCase?.Icon ?? PhoneArrowUpRightIcon,
    };
});

export const GENERIC_SIGNUP_SLUG = GENERIC_CAMPAIGN;

/** The industry picker shown on `/get-started`, in catalogue order. */
export const INDUSTRY_CHOICES: readonly SignupChoice[] = SIGNUP_CAMPAIGNS.filter(
    (campaign) => campaign.industry !== null,
).map((campaign) => ({
    id: campaign.industry as string,
    label: useCaseFor(campaign.industry)?.shortLabel ?? campaign.id,
}));

/** Resolve a `/get-started/:campaignSlug` segment. Undefined means the generic page. */
export function signupCampaignBySlug(slug: string | undefined): SignupCampaign | null {
    if (!slug) return genericSignupCampaign();
    return SIGNUP_CAMPAIGNS.find((campaign) => campaign.slug === slug) ?? null;
}

/**
 * The campaign whose agent copy fits a vertical.
 *
 * Keyed on the industry rather than the campaign the visitor arrived on, so
 * a `/get-started` lead who said "restaurant" gets the restaurant blueprint
 * and agent wording, not the generic one.
 */
export function signupCampaignForIndustry(industry: string | null | undefined): SignupCampaign | null {
    if (!industry) return null;
    return SIGNUP_CAMPAIGNS.find((campaign) => campaign.industry === industry) ?? null;
}

export function genericSignupCampaign(): SignupCampaign {
    // Non-null by construction: the generic seed is the last CAMPAIGN_SEEDS entry.
    return SIGNUP_CAMPAIGNS.find((campaign) => campaign.id === GENERIC_CAMPAIGN) as SignupCampaign;
}

export function signupPathFor(campaign: SignupCampaign, search = "", hash = ""): string {
    const query = search ? (search.startsWith("?") ? search : `?${search}`) : "";
    const fragment = hash ? (hash.startsWith("#") ? hash : `#${hash}`) : "";
    const base = campaign.id === GENERIC_CAMPAIGN ? "/get-started" : `/get-started/${campaign.slug}`;
    return `${base}${query}${fragment}`;
}

/** Every `/get-started` path, for the route table and the noindex check. */
export const SIGNUP_PATHS: readonly string[] = [
    "/get-started",
    ...SIGNUP_CAMPAIGNS.filter((campaign) => campaign.id !== GENERIC_CAMPAIGN).map(
        (campaign) => `/get-started/${campaign.slug}`,
    ),
];
